import { useState, useEffect } from 'react'
import DataGrid from '../components/DataGrid';

import { getCharacteristicsList, characteristicsDelete, getCharacteristicAll } from '../services/characteristics';

import Button from '@mui/material/Button';
import { Box, FormLabel, Grid, Menu, MenuItem, Paper, Select, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { useSnackbar } from 'notistack';
import SearchBar from '../components/SearchBar';
import TitleText from '../components/TitleText';
import CreateCharacteristicModal from '../components/CreateCharacteristicModal';
import { getProductVariantsList } from '../services/productVariantsService';
import { getProductsList } from '../services/productService';
import ProductGrid from '../components/ProductGrid';
import CharacteristicAssignmentModal from '../components/CharacteristicAssignmentModal';
import { characteristicCategoryIcons } from '../constants/characteristicCategoryIcons';
import DependencyGrid from '../components/DependencyGrid';
import CharacteristicDependencyModal from '../components/CharacteristicDependencyModal';
import ButtonAdd from '../components/ButtonAdd';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShowLoading from '../components/ShowLoading';


const tableStyle = {
  headerCell: {
    color: '#A1A1A1', 
    borderBottom: '0', 
    padding: '0.5rem 0.5rem',
    fontSize:'0.8125rem',
    width:'20%'
  },
  headerRow:{
    borderBottom:'1px solid #A1A1A1'
  },
  bodyCell:{
    padding: '1rem 0.5rem',
    width: 'calc( 100% / 3)',
    borderBottom: '1px solid #F8F8F8',
    maxWidth: 0
  },
  preventOverflow: {
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  twoLinesOverflow: {
    fontSize: '0.875rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
  }
};

const missingColor = '#e31212ff'


const characteristicActions = ({onEdit, onDelete, rowItem}) => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <IconButton sx={{ padding: 0 }} onClick={() => onEdit(rowItem)}>
          <EditOutlinedIcon style={{ fontSize: {lg:'1.5rem'}, color: 'black'}}/>
        </IconButton>
        <IconButton sx={{ padding: 0 }} onClick={() => onDelete(rowItem)}>
          <DeleteOutlineOutlinedIcon style={{ fontSize: {lg:'1.5rem'}, color: 'red'}} />
        </IconButton>
      </Box>
    </>
  )
}


const CharacteristicPage = () => {

  const [ searchTerm, setSearchTerm ] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [ tableData, setTableData ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ lastEvaluatedKey, setLastEvaluatedKey ] = useState(null);
  const [ paginationKeys, setPaginationKeys ] = useState([null]); // Historial de keys por página
  const [ itemToEdit, setItemToEdit ] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  const [sortAscending, setSortAscending] = useState(true)
  const [ filteredCharacteristic, setFilteredCharacteristic ] = useState("")

  const [ characteristicFilterOptions, setCharacteristicFilterOptions ] = useState([])

  const [activeTab, setActiveTab] = useState(1)

  const tabs = [
    { index: 1, icon: <DescriptionIcon sx={{ fontSize: "2rem", color: '#2C2C2C' }} />, title: "Alta de Características" },
    { index: 2, icon: <FactCheckIcon sx={{ fontSize: "2rem", color: '#2C2C2C' }} />, title: "Asignación de Características" },
    { index: 3, icon: <AccountTreeIcon sx={{ fontSize: "2rem", color: '#2C2C2C' }} />, title: "Configurar Dependencias" }
  ]

  // console.log(characteristicCategoryIcons)

  const { enqueueSnackbar } = useSnackbar();

  // Reset pagination when search parameters change
  useEffect(() => {
    setPage(1);
    setPaginationKeys([null]);
    setTotalPages(1);
  }, [searchTerm, rowsPerPage, activeTab, sortAscending, filteredCharacteristic]);

  useEffect(() => {
    getTableData();
  }, [searchTerm, page, rowsPerPage, activeTab, sortAscending, filteredCharacteristic]);
  
  const getTableData = async() => {
    setIsLoading(true);

    let items = [];
    let charFilterOptions = []
    let resp
    
    // Obtener el lastEvaluatedKey correspondiente a la página actual
    const currentPageKey = paginationKeys[page - 1] || null;

    if (activeTab === 1) {
        resp = await getCharacteristicsList(
          searchTerm,
          page,
          rowsPerPage,
          currentPageKey,
          sortAscending
        );

      } else{
        resp = await getProductsList({
          searchTerm: searchTerm,
          page: page,
          rowsPerPage: rowsPerPage,
          lastEvaluatedKey: currentPageKey,
          sortAscending: sortAscending,
          filteredCharacteristic: filteredCharacteristic,
          fullSearch: false,
          ignoredFields: ['variantMatrix']
        });
      }

    items = resp?.items || [];
    const newLastEvaluatedKey = resp?.lastEvaluatedKey || null;

    if (activeTab === 2 || activeTab === 3) {
      charFilterOptions = await getCharacteristicAll();
      
      setCharacteristicFilterOptions(charFilterOptions?.items || [])
    }

    setTableData(items);
    setLastEvaluatedKey(newLastEvaluatedKey);
    
    // Actualizar el historial de keys de paginación
    if (newLastEvaluatedKey && !paginationKeys[page]) {
      const newKeys = [...paginationKeys];
      newKeys[page] = newLastEvaluatedKey;
      setPaginationKeys(newKeys);
      // Si hay más páginas disponibles, actualizar totalPages
      setTotalPages(page + 1);
    } else if (!newLastEvaluatedKey && page === totalPages) {
      // Si no hay más páginas, mantener el totalPages actual
      setTotalPages(page);
    }
    
    setIsLoading(false);
  };

  const onEdit = async(item) => {
    setItemToEdit(item);
    setOpenModal(true);
  };

  const onDelete = async(item) => {
      const deleteResult = await characteristicsDelete(item._id);
      if (deleteResult.result){
        enqueueSnackbar("Eliminación exitosa", {
          variant: 'success'
        });
        await getTableData();
      } else{
        enqueueSnackbar(deleteResult.message, {
          variant: 'error'
        });
      }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    setPaginationKeys([null]); // Reset pagination keys
    setTotalPages(1);
  };

  const handleChangeSearchTerm = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(1);
    setPaginationKeys([null]); // Reset pagination keys
    setTotalPages(1);
  };

  const onCloseModal = async () =>{
    setOpenModal(false);
    !!itemToEdit && setItemToEdit(null);
    
    await getTableData();
  };

  const showCharacteristicModifierIcon = (char) => {
    if (!char?.characteristicCategory) {
        return (
            <Box
                sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                }}
            >
                <ErrorOutlineIcon sx={{ fontSize: {lg:'1.5rem'}, color: missingColor}}/>
            </Box>
        )
    }

    const characteristicIcons = characteristicCategoryIcons.find((item) => item.value === char?.characteristicCategory || null)
    return characteristicIcons.iconComponent

  }

  const tabOption = ({ index, icon, title }) => {
    return(
      <Button
        sx={{ 
          display: "flex",
          flex: "row",
          flexWrap: "nowrap",
          gap: 2,
          height: "4rem", 
          minWidth: "16rem",
          alignItems: "center",
          justifyContent: "center", 
          border: 1, 
          borderColor: '#FC581F', 
          background: index === activeTab ? "#FC581F50" : "white",
          p:{xs:' 0.688rem 0.688rem', lg:'6px 16px'},
          borderRadius:'0.25rem',
          '&:hover': { 
            boxShadow: 4,
            backgroundColor: '#FC581F'
          }
        }}
        onClick={() => { 
          if (activeTab !== index) {
            setPage(1);
            setLastEvaluatedKey(null)
            setPaginationKeys([null]) // Reset pagination keys
            setTotalPages(1)
            setSearchTerm("")
            setActiveTab(index)

            // Reset characteristic filter on tab change
            if (index === 2 || index === 3)
              setFilteredCharacteristic("")
          }
        }}
      >
        {icon}
        <Typography
          sx={{
            fontSize: { xs: '0.8rem', md: '1rem' },
            fontWeight: 500,
            color: '#2C2C2C',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
      </Button>
    )
  }

const baseLayout = ({ title, handleSearch, searchPlaceholder, actionTitle }) => {
  return (
    <>
      <TitleText title={title} />
      <Box
        sx={{
          width: '100%',
          pb: { xs: '1.5rem', md: '2rem', lg: '2.13rem' }
        }}
      >
        <Grid container spacing={2}>
          {/* SearchBar Full Width on xs, Half on md+ */}
          <Grid item xs={12} md={6}>
            <SearchBar onSearchTermChange={handleSearch} placeHolder={searchPlaceholder} />
          </Grid>

          {/* Right Section */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {/* Sort Dropdown */}
              <Grid item xs={12} md={activeTab === 2 || activeTab === 3 ? 6 : 12}>
                <Select
                  fullWidth
                  sx={{ backgroundColor: "white" }}
                  value={sortAscending}
                  onChange={(e) => setSortAscending(e.target.value)}
                >
                  <MenuItem value={true}><ArrowUpwardIcon /> Ordenar A-Z</MenuItem>
                  <MenuItem value={false}><ArrowDownwardIcon /> Ordenar Z-A</MenuItem>
                </Select>
              </Grid>

              {/* Filter Dropdown (only if tab is 2 or 3) */}
              {(activeTab === 2 || activeTab === 3) && (
                <Grid item xs={12} md={6}>
                  <Select
                    displayEmpty
                    fullWidth
                    sx={{ backgroundColor: "white" }}
                    value={filteredCharacteristic}
                    onChange={(e) => setFilteredCharacteristic(e.target.value)}
                  >
                    <MenuItem value={null} disabled>Filtrar por Característica</MenuItem>
                    <MenuItem value={""}>Todos</MenuItem>
                    {characteristicFilterOptions?.map((char, index) => (
                      <MenuItem key={index} value={char._id}>{char.characteristicName}</MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}

              {/* Button - new row on small screens */}
              {actionTitle && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <ButtonAdd
                      onClick={() => setOpenModal(true)}
                      title={actionTitle}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};


  return (
    <Box sx={{px:{xs:'1.5rem', md:'3rem'}}} >
      <Box sx={{ display: "flex", flexDirection: "row", p: "2rem", justifyContent: "center" }} >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "row",
            gap: 4,
            p: "1rem",
            justifyContent: "center",
          }}
          component={Paper}
        >
          {tabs.map(t => (
            <Box key={t.index}>
              {tabOption({ index: t.index, icon: t.icon, title: t.title })}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: {xs: "block", md: "none"} }}>
          <Select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            fullWidth
            sx={{ backgroundColor: "#ffff" }}
          >
            {
              tabs.map(t => (
                <MenuItem key={t.index} value={t.index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {t.icon}
                    {t.title}
                  </Box>
                </MenuItem>
              ))
            }
          </Select>
        </Box>
      </Box>

      {activeTab === 1 && (
        <>
          {
            baseLayout({
              title: 'CARACTERÍSTICAS.', 
              handleSearch: handleChangeSearchTerm, 
              searchPlaceholder: "Buscar Característica", 
              actionTitle: "Agregar característica"
            })
          }
          {
            isLoading 
            ? <ShowLoading /> 
            : <DataGrid
                tableData={tableData}
                page={page}
                rowsPerPage={rowsPerPage}
                totalPages={totalPages}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onEdit={onEdit} 
                onDelete={onDelete} 
                onVariants={() => {}} 
                isLoading={isLoading} 
                tableStyle={tableStyle}
                characteristicCategories={characteristicCategoryIcons}
                actions={characteristicActions}
            />
          }
          {
            openModal &&
            <CreateCharacteristicModal 
              reloadTableData={getTableData} 
              itemToEdit={itemToEdit} 
              openModal={openModal} 
              onCloseModal={onCloseModal}
              characteristicCategories={characteristicCategoryIcons}
            />
          }
        </>
      )}

      { activeTab === 2 && (
        <>
          {
            baseLayout({
              title: 'ASIGNACIÓN DE CARACTERÍSTICAS', 
              handleSearch: handleChangeSearchTerm, 
              searchPlaceholder: "Buscar Producto", 
            })
          }
          {
            isLoading 
            ? <ShowLoading /> 
            : <ProductGrid 
              tableData={tableData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalPages={totalPages}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              onEdit={onEdit} 
              onVariants={() => {}} 
              isLoading={isLoading} 
              tableStyle={tableStyle}
              showCharacteristicIcons={showCharacteristicModifierIcon}
            />
          }
          {
            openModal &&
            <CharacteristicAssignmentModal 
              openModal={openModal} 
              itemToEdit={itemToEdit} 
              onCloseModal={onCloseModal} 
              showCharacteristicIcons={showCharacteristicModifierIcon}
              onConfirmed={getTableData}
            />
          }
        </>
      )}

      { activeTab === 3 && (
        <>
          {
            baseLayout({
              title: 'CONFIGURAR DEPENDENCIAS', 
              handleSearch: handleChangeSearchTerm, 
              searchPlaceholder: "Buscar Producto", 
            })
          }
          {
            isLoading 
            ? <ShowLoading /> 
            : <ProductGrid 
                tableData={tableData}
                page={page}
                rowsPerPage={rowsPerPage}
                totalPages={totalPages}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onEdit={onEdit} 
                onVariants={() => {}} 
                isLoading={isLoading} 
                tableStyle={tableStyle}
                showCharacteristicIcons={showCharacteristicModifierIcon}
                isVariant={true}
            />
          }
          {
            openModal &&
            <CharacteristicDependencyModal 
              openModal={openModal} 
              itemToEdit={itemToEdit} 
              onCloseModal={onCloseModal} 
              onConfirmed={getTableData}
            />
          }
        </>
      )}
    </Box>
  )
}

export default CharacteristicPage;